import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Check, ChevronLeft, ChevronRight } from 'lucide-react';
// Types
interface Review {
  id: number;
  text: string;
  author: string;
  country: string;
}
const reviews: Review[] = [{
  id: 1,
  text: 'En 7 días vi clarísimo en qué se iba todo. Dejé de improvisar.',
  author: 'Gabriela Z.',
  country: 'Colombia'
}, {
  id: 2,
  text: 'Me dejó de dar ansiedad abrir la app del banco.',
  author: 'Carolina M.',
  country: 'México'
}, {
  id: 3,
  text: 'Sin Excel. Solo 10 minutos y ya siento control.',
  author: 'Andrea R.',
  country: 'Perú'
}, {
  id: 4,
  text: "Con lo de los sobres ya no mezclo todo y no 'desaparece'.",
  author: 'Lucía F.',
  country: 'Chile'
}, {
  id: 5,
  text: 'Corté 3 fugas el mismo día. Eso me enganchó.',
  author: 'María P.',
  country: 'Argentina'
}, {
  id: 6,
  text: 'La regla de 24h me frenó un montón de antojos.',
  author: 'Daniela S.',
  country: 'Ecuador'
}, {
  id: 7,
  text: 'Pagué mínimos y ataqué una deuda. Por fin vi avance.',
  author: 'Sofía L.',
  country: 'Guatemala'
}, {
  id: 8,
  text: 'Ya no llego en rojo a fin de quincena. Cambió todo.',
  author: 'Valentina R.',
  country: 'Colombia'
}];
export function ReviewsCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(1);
  const [direction, setDirection] = useState(0);
  // Responsive handler
  useEffect(() => {
    const handleResize = () => {
      setItemsPerPage(window.innerWidth >= 768 ? 2 : 1);
    };
    // Initial check
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Auto-rotation
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      nextSlide();
    }, 5000); // 5 seconds
    return () => clearInterval(interval);
  }, [currentIndex, isPaused, itemsPerPage]);
  const nextSlide = useCallback(() => {
    setDirection(1);
    setCurrentIndex(prev => (prev + 1) % reviews.length);
  }, []);
  const prevSlide = useCallback(() => {
    setDirection(-1);
    setCurrentIndex(prev => (prev - 1 + reviews.length) % reviews.length);
  }, []);
  // Get visible reviews based on current index and items per page
  const getVisibleReviews = () => {
    const items = [];
    for (let i = 0; i < itemsPerPage; i++) {
      const index = (currentIndex + i) % reviews.length;
      items.push(reviews[index]);
    }
    return items;
  };
  const visibleReviews = getVisibleReviews();
  // Animation variants
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 100 : -100,
      opacity: 0
    })
  };
  return <section className="py-12 bg-gradient-to-br from-[#1a4731] to-[#0f291e] overflow-hidden relative" aria-label="Reseñas de clientes" data-id="element-190">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-191">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-192"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-brand-green/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-193"></div>
      </div>

      <div className="container mx-auto px-4 max-w-6xl relative z-10" data-id="element-194">
        <div className="text-center mb-12" data-id="element-195">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 drop-shadow-sm" data-id="element-196">
            ⭐ Reseñas con calificación
          </h2>
          <p className="text-white/80 text-lg max-w-2xl mx-auto" data-id="element-197">
            Historias reales de personas que recuperaron el control de su dinero
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} data-id="element-198">
          {/* Main Carousel Area */}
          <div className="overflow-hidden px-4 md:px-12 py-4 min-h-[300px]" data-id="element-199">
            <div className={`grid ${itemsPerPage === 2 ? 'grid-cols-2' : 'grid-cols-1'} gap-6 md:gap-8`} data-id="element-200">
              <AnimatePresence initial={false} mode="popLayout" custom={direction} data-id="element-201">
                {visibleReviews.map(review => <motion.div key={`${review.id}-${currentIndex}`} // Unique key forces re-render for animation
              custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
                x: {
                  type: 'spring',
                  stiffness: 300,
                  damping: 30
                },
                opacity: {
                  duration: 0.2
                }
              }} className="h-full" data-id="element-202">
                    <div className="bg-brand-cream h-full p-6 md:p-8 rounded-2xl shadow-xl border-2 border-brand-green/20 flex flex-col relative group hover:border-brand-green/40 transition-colors duration-300" data-id="element-203">
                      {/* Stars */}
                      <div className="flex gap-1 mb-4" data-id="element-204">
                        {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-brand-gold text-brand-gold" data-id="element-205" />)}
                      </div>

                      {/* Verified Badge */}
                      <div className="absolute top-6 right-6 flex items-center gap-1 bg-white px-2 py-1 rounded-full border border-gray-100 shadow-sm" data-id="element-206">
                        <div className="bg-green-500 rounded-full p-0.5" data-id="element-207">
                          <Check className="w-2 h-2 text-white" strokeWidth={4} data-id="element-208" />
                        </div>
                        <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wide" data-id="element-209">
                          Compra verificada
                        </span>
                      </div>

                      {/* Content */}
                      <blockquote className="flex-grow mb-6" data-id="element-210">
                        <p className="font-sans text-lg text-brand-dark italic leading-relaxed" data-id="element-211">
                          "{review.text}"
                        </p>
                      </blockquote>

                      {/* Author */}
                      <div className="mt-auto flex items-center gap-3 border-t border-brand-green/10 pt-4" data-id="element-212">
                        <div className="w-10 h-10 rounded-full bg-brand-green/10 flex items-center justify-center text-brand-green font-bold text-lg" data-id="element-213">
                          {review.author.charAt(0)}
                        </div>
                        <div data-id="element-214">
                          <p className="font-heading font-bold text-brand-dark text-sm" data-id="element-215">
                            {review.author}
                          </p>
                          <p className="text-xs text-brand-text/60 font-medium" data-id="element-216">
                            {review.country}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>)}
              </AnimatePresence>
            </div>
          </div>

          {/* Navigation Arrows */}
          <button onClick={prevSlide} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-6 bg-white text-brand-dark p-3 rounded-full shadow-lg hover:bg-brand-green hover:text-white transition-all duration-300 z-20 focus:outline-none focus:ring-2 focus:ring-brand-gold" aria-label="Reseña anterior" data-id="element-217">
            <ChevronLeft className="w-6 h-6" data-id="element-218" />
          </button>

          <button onClick={nextSlide} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-6 bg-white text-brand-dark p-3 rounded-full shadow-lg hover:bg-brand-green hover:text-white transition-all duration-300 z-20 focus:outline-none focus:ring-2 focus:ring-brand-gold" aria-label="Siguiente reseña" data-id="element-219">
            <ChevronRight className="w-6 h-6" data-id="element-220" />
          </button>

          {/* Dots Navigation */}
          <div className="flex justify-center gap-2 mt-8" data-id="element-221">
            {reviews.map((_, idx) => <button key={idx} onClick={() => {
            setDirection(idx > currentIndex ? 1 : -1);
            setCurrentIndex(idx);
          }} className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${idx === currentIndex ? 'bg-brand-gold w-8' : 'bg-white/30 hover:bg-white/60'}`} aria-label={`Ir a reseña ${idx + 1}`} data-id="element-222" />)}
          </div>
        </div>
      </div>
    </section>;
}